import { useEffect, useState } from "react";
import { apiConstants, postGeneric } from "../../common/constants";
import { Box, Card, CardContent, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { formatDate } from "../../common/util";
import { FormProvider, RHFTextField } from "../../common/hook-form";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import PageTitle from "../../components/PageTitle";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../../redux/commonApiCalls";
import Loader from "../../common/Loader";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { FieldLabel, FilterField } from "../../common/FilterBar";
import OrderRowActions from "../../common/OrderRowActions";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px' }}>
        {children}
    </Box>
);

const STATUS_MAP = {
    ACTIVE:    { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0', dot: '#16a34a' },
    INACTIVE:  { bg: '#fef2f2', fg: '#dc2626', border: '#fecaca', dot: '#dc2626' },
    PENDING:   { bg: '#fffbeb', fg: '#b45309', border: '#fde68a', dot: '#d97706' },
};

/**
 * Only links a browser can open. The upload stored a failed image fetch as null, and old rows hold
 * values like "google.com" — each one listed as "View Image N" with nowhere to go.
 */
const openableImages = (images) =>
    (images || []).filter((u) => typeof u === 'string' && /^https?:\/\/\S+$/i.test(u.trim()));

/**
 * Shown in the page, not opened as a link: images copied from a link with no extension (e.g. a Google
 * thumbnail) were stored as application/octet-stream, which a new tab downloads as an unreadable file
 * but an <img> still displays. A link that is not an image at all falls back to the link itself.
 */
function SkuImage({ url, index }) {
    const [failed, setFailed] = useState(false);
    return (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '6px', bgcolor: '#f8fafc', p: 1 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569', mb: 0.75 }}>Image {index + 1}</Typography>
            {failed ? (
                <Box component="a" href={url} target="_blank" rel="noreferrer"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#1d4ed8' }}>
                    <ImageOutlinedIcon sx={{ fontSize: 16 }} />
                    Could not show this image. Open the link
                </Box>
            ) : (
                <Box component="img" src={url} alt={`SKU image ${index + 1}`} onError={() => setFailed(true)}
                    sx={{ display: 'block', maxWidth: '100%', maxHeight: 360, mx: 'auto', borderRadius: '4px' }} />
            )}
        </Box>
    );
}

function StatusBadge({ status }) {
    if (!status) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
    const p = STATUS_MAP[status?.toUpperCase()] || { bg: '#f3f4f6', fg: '#374151', border: '#e5e7eb', dot: '#9ca3af' };
    return (
        <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            px: '9px', py: '4px', borderRadius: '20px',
            fontSize: '11px', fontWeight: 700, letterSpacing: '0.3px',
            bgcolor: p.bg, color: p.fg,
            border: `1px solid ${p.border}`,
            maxWidth: '100%', whiteSpace: 'normal', lineHeight: 1.3,
        }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.dot, flexShrink: 0 }} />
            {/* "APPROVAL_REQUIRED" has no break point and ran over the next column. */}
            {String(status).replace(/_/g, ' ')}
        </Box>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────

function WeightFreezeDetails() {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [selectedImages, setSelectedImages] = useState([]);
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [filters, setFilters] = useState([]);

    const methods = useForm({ defaultValues: { merchant: '', sku: '' } });
    const { handleSubmit, reset } = methods;
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery();

    const columns = [
        {
            field: 'merchantId',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => (
                <CellLine bold>{merchants?.find(m => m.id === row?.merchantId)?.name || '—'}</CellLine>
            ),
        },
        {
            field: 'sku',
            headerName: 'SKU', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 130, flex: 0.8,
            renderCell: ({ row }) => <CellLine bold>{row.sku || '—'}</CellLine>,
        },
        {
            field: 'status',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 170, flex: 0.8,
            renderCell: ({ row }) => <StatusBadge status={row.status} />,
        },
        {
            field: 'weight',
            headerName: 'Weight & Dimensions', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 230, flex: 1.4,
            renderCell: ({ row }) => {
                const deadKg = row?.weight ? (row.weight / 1000).toFixed(2) : null;
                const volKg  = (row?.length && row?.breadth && row?.height)
                    ? ((row.length * row.breadth * row.height) / 5000).toFixed(2)
                    : null;
                const dims = (row?.length && row?.breadth && row?.height)
                    ? `${Number(row.length).toFixed(2)} × ${Number(row.breadth).toFixed(2)} × ${Number(row.height).toFixed(2)} cm`
                    : null;
                const chip = (label, value, bg, color, border) => (
                    <Box component="span" sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        px: '7px', py: '2px', borderRadius: '4px',
                        fontSize: '11px', fontWeight: 600,
                        bgcolor: bg, color, border: `1px solid ${border}`,
                        whiteSpace: 'nowrap',
                    }}>
                        <Box component="span" sx={{ fontSize: '9px', fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</Box>
                        {value}
                    </Box>
                );
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', py: 0.75 }}>
                        {chip('Dead', deadKg ? `${deadKg} kg` : '—', '#dbeafe', '#1d4ed8', '#bfdbfe')}
                        {chip('Vol',  volKg  ? `${volKg} kg`  : '—', '#dcfce7', '#15803d', '#bbf7d0')}
                        {chip('Dims', dims || '—', '#ede9fe', '#6d28d9', '#c4b5fd')}
                    </Box>
                );
            },
        },
        {
            field: 'createdDate',
            headerName: 'Created', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 160, flex: 1,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine muted>{formatDate(row?.createdDate)}</CellLine>
                    {row?.createdByUser && <CellLine muted>{row.createdByUser}</CellLine>}
                </CellStack>
            ),
        },
        {
            field: 'lastModifiedDate',
            headerName: 'Last Modified', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 130, flex: 0.9,
            renderCell: ({ row }) => <CellLine muted>{row?.lastModifiedDate ? formatDate(row.lastModifiedDate) : '—'}</CellLine>,
        },
        {
            field: 'images',
            headerName: '', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => {
                const images = openableImages(row?.images);
                const count = images.length;
                if (!count) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                return (
                    <OrderRowActions
                        actions={[{
                            key: 'images',
                            label: `View images (${count})`,
                            icon: <ImageOutlinedIcon fontSize="small" />,
                            onClick: () => { setSelectedImages(images); setOpenImageDialog(true); },
                        }]}
                    />
                );
            },
        },
    ];

    useEffect(() => {
        setIsLoading(true);
        postGeneric(apiConstants.REPORT_GENERIC_SEARCH, {
            collection: "weight_freeze",
            limit: 10000,
            skipCount: true,
            andfilter: filters
        }).then((res) => {
            setData(res?.data?.data);
        }).catch((err) => {
            enqueueSnackbar("Some error occurred!", { variant: 'error' });
            console.log(err);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [filters]); // eslint-disable-line

    const onSubmit = (vals) => {
        const andfilter = [];
        if (vals.merchant) andfilter.push({ "field": "merchantId", "operator": "eq", "value": vals.merchant });
        if (vals.sku) andfilter.push({ "field": "sku", "operator": "eq", "value": vals.sku });
        setFilters(andfilter);
    };

    const handleCloseImageDialog = () => { setOpenImageDialog(false); setSelectedImages([]); };

    return (
        <>
            {isLoading && <Loader />}
            <PageTitle>Courier Weight Freeze Details</PageTitle>

            {/* ── Filter bar ────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>SKU Name</FieldLabel>
                                <RHFTextField name="sku" label="" placeholder="SKU Name" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Box component="button" type="submit"
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </Box>
                                <Box component="button" type="button"
                                    onClick={() => { reset(); setFilters([]); }}
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                                    Reset
                                </Box>
                            </Box>

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={data ? data.map((val, index) => ({ ...val, id: index })) : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    paginationMode={"client"}
                    hideFooterPagination={false}
                    getRowHeight={() => 'auto'}
                    rowCount={data?.length || 0}
                    getRowId={(row) => row?.id}
                />
            </Box>

            {/* ── Image dialog ──────────────────────────────────────────── */}
            <Dialog open={openImageDialog} onClose={handleCloseImageDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>
                        SKU Images ({selectedImages?.length})
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {selectedImages?.map((imageUrl, index) => (
                        <SkuImage key={index} url={imageUrl} index={index} />
                    ))}
                </DialogContent>
            </Dialog>
        </>
    );
}

export default WeightFreezeDetails;
